import type { PillarPosition, FourPillars } from '../types/pillars';
import type { SinsalEntry, GeneralSinsalName } from '../types/sinsal';
import { stemIndex, branchIndex } from '../core/sexagenary';
import {
  CHEONUL_GUIIN, YANGIN_SAL, KUIGANG_INDICES,
  getGongmang, getSamhapGroup,
  MUNCHANG_GUIIN, HONGYEOM_SAL, TAEGUK_GUIIN,
  WOLDEOK_GUIIN, CHEONDEOK_GUIIN, CHEONJU_GUIIN,
  HYEONCHIM_STEMS, HYEONCHIM_BRANCHES, GEUMYEOROK, BAEKHO_SAL_INDICES,
  CHEOLLA_BRANCHES, JIMANG_BRANCHES,
  HAKDANG_GUIIN, GWANGUI_HAKGWAN, CHEONBOK_GUIIN,
  BOKSEONG_GUIIN, MUNGOK_GUIIN, AMROK, JAEGO_GUIIN,
  BIIN_SAL, YUHA_SAL, GEUPGAK_SAL, WONJIN_SAL,
  GUIMUN_SAL, CHEONHUI_SEONG, HWANGEUN_DAESA,
  GOSIN_SAL, GWASUK_SAL, getSeasonGroup, CHEONGWAN_GUIIN,
  EUMCHAK_SAL_INDICES, YANGCHA_SAL_INDICES,
  TANGHWA_SAL,
  // 신규 조견표
  GUNROK, OMYO_SAL, GORAN_INDICES, PALJEON_INDICES,
  HONGRAN_SAL,
  ANMAENG_SAL, DANMYEONG_SAL, JIKNAN_SAL, YAJE_SAL,
  MUJEONG_SAL, SAGYE_SAL, YEOMWANG_SAL, GEONGAK_SAL,
  CHEONSO_SAL,
  // sal06/sal13/sal14/sal16 batch
  YUKSU_INDICES, HYOSIN_INDICES, YANGJIN_INDICES, EUMJIN_INDICES,
  PYEONGDU_INDICES, GEUMSIN_INDICES,
  WOLHAP, CHEONOK_SAL,
  DAEPAE_SAL, PALPAE_SAL, NANGJEOK_SAL, JEOLBANG_SAL,
  CHEONMO_SAL, TAMO_SAL, NONGA_SAL,
  MAENGIN_SAL, SUHWA_SAL, SIMSU_SAL, JANGGUN_SAL, HYEOLBUN_SAL,
  CHEONHYEOK_INDICES,
  SANGCHUNG_SAL,
  GOLPA_SAL_MALE, GOLPA_SAL_FEMALE,
  CHEOLSO_SAL_MALE, CHEOLSO_SAL_FEMALE,
  JAEGA_SAL, GYEOLHANG_INDICES, POKPAE_SAL,
  getNapeumOhaeng, JISO_SAL, SINEUM_SAL,
  // 관살류 14종
  SUOK_SAL, TANHAM_SAL, DANGYO_SAL, SAMGU_SAL,
  NAKJEONG_SAL, CHEOLSA_SAL, CHEONJO_SAL, YACHE_SAL,
  GYEBI_SAL, CHWIMYEONG_SAL,
  SAMWI_YEOK, SAMWI_JEONG, SAMWI_GO,
} from './definitions';

const ALL_PILLAR_POSITIONS: PillarPosition[] = ['year', 'month', 'day', 'hour'];

/** 귀문관살 체크 대상 기둥쌍 (모든 조합) */
const ALL_GUIMUN_PAIRS: readonly [PillarPosition, PillarPosition][] = [
  ['year', 'month'], ['year', 'day'], ['year', 'hour'],
  ['month', 'day'], ['month', 'hour'],
  ['day', 'hour'],
];

/** 시주 미포함 시 귀문관살 체크 대상 기둥쌍 */
const ALL_GUIMUN_PAIRS_NO_HOUR = ALL_GUIMUN_PAIRS.filter(([a, b]) => a !== 'hour' && b !== 'hour');

/** 자결살 충 쌍 (子↔午, 卯↔酉) */
const JAGYEOL_PAIRS: readonly [number, number][] = [[0, 6], [3, 9]];

/** 공망 기둥별 이름 매핑 */
const GONGMANG_NAMES: Record<PillarPosition, GeneralSinsalName> = {
  year: '[년]공망', month: '[월]공망', day: '[일]공망', hour: '[시]공망',
};

/** 일주 sexagenary 인덱스 기반 신살 설정 (인덱스 배열 → 신살명) */
const DAY_PILLAR_SEXAGENARY_SINSAL: readonly (readonly [readonly number[], GeneralSinsalName])[] = [
  [EUMCHAK_SAL_INDICES, '음착살'],
  [YANGCHA_SAL_INDICES, '양차살'],
  [GORAN_INDICES, '고란살'],
  [PALJEON_INDICES, '팔전살'],
  [YUKSU_INDICES, '육수'],
  [HYOSIN_INDICES, '효신살'],
  [YANGJIN_INDICES, '양진관살'],
  [EUMJIN_INDICES, '음진관살'],
  [PYEONGDU_INDICES, '평두관살'],
  [GEUMSIN_INDICES, '금신살'],
];

/** 헬퍼 함수 공유 컨텍스트 */
interface SinsalContext {
  fourPillars: FourPillars;
  positions: PillarPosition[];
  /** 각 기둥의 천간 인덱스 (사전 계산) */
  stemIndices: Record<PillarPosition, number>;
  /** 각 기둥의 지지 인덱스 (사전 계산) */
  branchIndices: Record<PillarPosition, number>;
  yearSeasonGroup: number;
  monthSeasonGroup: number;
  yearSamhapGroup: number;
  noHour?: boolean;
  gender?: 'male' | 'female';
}

/** 일간 기준 신살 */
function findDayStemSinsal(ctx: SinsalContext): SinsalEntry[] {
  const entries: SinsalEntry[] = [];
  const { positions, stemIndices, branchIndices } = ctx;
  const dayStemIdx = stemIndices.day;

  // 천을귀인 (일간 기준)
  const guiinBranches = CHEONUL_GUIIN[dayStemIdx];
  if (guiinBranches) {
    for (const pos of positions) {
      const bIdx = branchIndices[pos];
      if (guiinBranches.includes(bIdx)) {
        entries.push({ name: '천을귀인', pillar: pos, basedOn: 'day' });
      }
    }
  }

  // 양인살 (일간 기준, 전간 적용 — sal04 기준)
  const yanginBranch = YANGIN_SAL[dayStemIdx];
  if (yanginBranch !== undefined) {
    for (const pos of positions) {
      if (branchIndices[pos] === yanginBranch) {
        entries.push({ name: '양인살', pillar: pos, basedOn: 'day' });
      }
    }
  }

  // 문창귀인 (일간 기준)
  const munchangBranch = MUNCHANG_GUIIN[dayStemIdx];
  for (const pos of positions) {
    const bIdx = branchIndices[pos];
    if (bIdx === munchangBranch) {
      entries.push({ name: '문창귀인', pillar: pos, basedOn: 'day' });
    }
  }

  // 홍염살 (일간 기준)
  const hongyeomBranch = HONGYEOM_SAL[dayStemIdx];
  for (const pos of positions) {
    const bIdx = branchIndices[pos];
    if (bIdx === hongyeomBranch) {
      entries.push({ name: '홍염살', pillar: pos, basedOn: 'day' });
    }
  }

  // 태극귀인 (일간 기준)
  const taegukBranches = TAEGUK_GUIIN[dayStemIdx];
  if (taegukBranches) {
    for (const pos of positions) {
      const bIdx = branchIndices[pos];
      if (taegukBranches.includes(bIdx)) {
        entries.push({ name: '태극귀인', pillar: pos, basedOn: 'day' });
      }
    }
  }

  // 천주귀인 (일간 기준)
  const cheonjuBranch = CHEONJU_GUIIN[dayStemIdx];
  for (const pos of positions) {
    const bIdx = branchIndices[pos];
    if (bIdx === cheonjuBranch) {
      entries.push({ name: '천주귀인', pillar: pos, basedOn: 'day' });
    }
  }

  // 천관귀인 (일간 기준)
  const cheongwanBranch = CHEONGWAN_GUIIN[dayStemIdx];
  for (const pos of positions) {
    const bIdx = branchIndices[pos];
    if (bIdx === cheongwanBranch) {
      entries.push({ name: '천관귀인', pillar: pos, basedOn: 'day' });
    }
  }

  // 금여록 (일간 기준)
  const geumyeorokBranch = GEUMYEOROK[dayStemIdx];
  for (const pos of positions) {
    const bIdx = branchIndices[pos];
    if (bIdx === geumyeorokBranch) {
      entries.push({ name: '금여록', pillar: pos, basedOn: 'day' });
    }
  }

  // 학당귀인 (일간 기준, 장생지)
  const hakdangBranch = HAKDANG_GUIIN[dayStemIdx];
  for (const pos of positions) {
    if (branchIndices[pos] === hakdangBranch) {
      entries.push({ name: '학당귀인', pillar: pos, basedOn: 'day' });
    }
  }

  // 관귀학관 (일간 기준)
  const gwanguiBranch = GWANGUI_HAKGWAN[dayStemIdx];
  for (const pos of positions) {
    if (branchIndices[pos] === gwanguiBranch) {
      entries.push({ name: '관귀학관', pillar: pos, basedOn: 'day' });
    }
  }

  // 복성귀인 (일간 기준)
  const bokseongBranch = BOKSEONG_GUIIN[dayStemIdx];
  for (const pos of positions) {
    if (branchIndices[pos] === bokseongBranch) {
      entries.push({ name: '복성귀인', pillar: pos, basedOn: 'day' });
    }
  }

  // 문곡귀인 (일간 기준)
  const mungokBranch = MUNGOK_GUIIN[dayStemIdx];
  for (const pos of positions) {
    if (branchIndices[pos] === mungokBranch) {
      entries.push({ name: '문곡귀인', pillar: pos, basedOn: 'day' });
    }
  }

  // 암록 (일간 기준, 건록지의 육합)
  const amrokBranch = AMROK[dayStemIdx];
  for (const pos of positions) {
    if (branchIndices[pos] === amrokBranch) {
      entries.push({ name: '암록', pillar: pos, basedOn: 'day' });
    }
  }

  // 재고귀인 (일간 기준, 재성의 고지)
  const jaegoBranch = JAEGO_GUIIN[dayStemIdx];
  for (const pos of positions) {
    if (branchIndices[pos] === jaegoBranch) {
      entries.push({ name: '재고귀인', pillar: pos, basedOn: 'day' });
    }
  }

  // 비인살 (일간 기준, 양인의 충 위치)
  const biinBranch = BIIN_SAL[dayStemIdx];
  for (const pos of positions) {
    if (branchIndices[pos] === biinBranch) {
      entries.push({ name: '비인살', pillar: pos, basedOn: 'day' });
    }
  }

  // 유하살 (일간 기준)
  const yuhaBranch = YUHA_SAL[dayStemIdx];
  for (const pos of positions) {
    if (branchIndices[pos] === yuhaBranch) {
      entries.push({ name: '유하살', pillar: pos, basedOn: 'day' });
    }
  }

  // 건록 (일간 기준, 4주 전체)
  const gunrokBranch = GUNROK[dayStemIdx];
  for (const pos of positions) {
    if (branchIndices[pos] === gunrokBranch) {
      entries.push({ name: '건록', pillar: pos, basedOn: 'day' });
    }
  }

  // 탕화살 (일간 기준, sal16)
  const tanghwaBranch = TANGHWA_SAL[dayStemIdx];
  for (const pos of positions) {
    if (branchIndices[pos] === tanghwaBranch) {
      entries.push({ name: '탕화살', pillar: pos, basedOn: 'day' });
    }
  }

  // 낙정관살·계비관살·취명관살 (일간 기준 지지 매칭)
  const nakjeongBranch = NAKJEONG_SAL[dayStemIdx];
  const gyebiBranches = GYEBI_SAL[dayStemIdx];
  const chwimyeongBranches = CHWIMYEONG_SAL[dayStemIdx];
  for (const pos of positions) {
    const bIdx = branchIndices[pos];
    if (bIdx === nakjeongBranch) {
      entries.push({ name: '낙정관살', pillar: pos, basedOn: 'day' });
    }
    if (gyebiBranches.includes(bIdx)) {
      entries.push({ name: '계비관살', pillar: pos, basedOn: 'day' });
    }
    if (chwimyeongBranches.includes(bIdx)) {
      entries.push({ name: '취명관살', pillar: pos, basedOn: 'day' });
    }
  }

  return entries;
}

/** 일주 기준 신살 (sexagenary index 매칭) */
function findDayPillarSinsal(ctx: SinsalContext): SinsalEntry[] {
  const entries: SinsalEntry[] = [];
  const { fourPillars, positions, stemIndices, branchIndices } = ctx;
  const dayStemIdx = stemIndices.day;
  const dayBranchIdx = branchIndices.day;

  // 괴강살 (일주가 庚辰, 壬辰, 戊戌, 庚戌 중 하나여야 성립, 다른 기둥에 중첩 가능)
  if (KUIGANG_INDICES.includes(fourPillars.day.sexagenaryIndex)) {
    entries.push({ name: '괴강살', pillar: 'day', basedOn: 'day' });
    for (const pos of positions) {
      if (pos === 'day') continue;
      if (KUIGANG_INDICES.includes(fourPillars[pos].sexagenaryIndex)) {
        entries.push({ name: '괴강살', pillar: pos, basedOn: 'day' });
      }
    }
  }

  // 공망 (모든 기둥을 기준으로 검사, [년]/[월]/[일]/[시] 접두사)
  for (const base of positions) {
    const [gm1, gm2] = getGongmang(fourPillars[base].sexagenaryIndex);
    for (const pos of positions) {
      if (pos === base) continue;
      const bIdx = branchIndices[pos];
      if (bIdx === gm1 || bIdx === gm2) {
        entries.push({ name: GONGMANG_NAMES[base], pillar: pos, basedOn: base });
      }
    }
  }

  // 백호살 (모든 기둥의 육십갑자 인덱스 매칭)
  for (const pos of positions) {
    if (BAEKHO_SAL_INDICES.includes(fourPillars[pos].sexagenaryIndex)) {
      entries.push({ name: '백호살', pillar: pos, basedOn: pos });
    }
  }

  // 오묘살 (일간 → 일지 매칭, 일주 전용)
  if (OMYO_SAL[dayStemIdx] === dayBranchIdx) {
    entries.push({ name: '오묘살', pillar: 'day', basedOn: 'day' });
  }

  // 철사관살 (일간 → 일지 매칭, 일주 전용)
  const cheolsaBranches = CHEOLSA_SAL[dayStemIdx];
  if (cheolsaBranches && cheolsaBranches.includes(dayBranchIdx)) {
    entries.push({ name: '철사관살', pillar: 'day', basedOn: 'day' });
  }

  // 일주 sexagenary 인덱스 기반 신살 (음착·양차·고란·팔전·육수·효신·양진·음진·평두·금신)
  const daySexagenary = fourPillars.day.sexagenaryIndex;
  for (const [indices, name] of DAY_PILLAR_SEXAGENARY_SINSAL) {
    if (indices.includes(daySexagenary)) {
      entries.push({ name, pillar: 'day', basedOn: 'day' });
    }
  }

  // 지소살 및 신음살 (납음오행 기반, 일주 납음 기준)
  const dayNapeum = getNapeumOhaeng(daySexagenary);
  const jisoBranches = JISO_SAL[dayNapeum];
  const sineumBranch = SINEUM_SAL[dayNapeum];
  for (const pos of positions) {
    if (pos === 'day') continue;
    const bIdx = branchIndices[pos];
    if (jisoBranches.includes(bIdx)) {
      entries.push({ name: '지소관살', pillar: pos, basedOn: 'day' });
    }
    if (bIdx === sineumBranch) {
      entries.push({ name: '신음관살', pillar: pos, basedOn: 'day' });
    }
  }

  return entries;
}

/** 년지 기준 신살 */
function findYearBranchSinsal(ctx: SinsalContext): SinsalEntry[] {
  const entries: SinsalEntry[] = [];
  const { fourPillars, positions, branchIndices, yearSeasonGroup, yearSamhapGroup, noHour } = ctx;
  const yearBranchIdx = branchIndices.year;
  const dayBranchIdx = branchIndices.day;

  // 고신살 (년지의 계절 그룹 기준)
  const gosinBranch = GOSIN_SAL[yearSeasonGroup];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === gosinBranch) {
      entries.push({ name: '고신살', pillar: pos, basedOn: 'year' });
    }
  }

  // 과숙살 (년지의 계절 그룹 기준)
  const gwasukBranch = GWASUK_SAL[yearSeasonGroup];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === gwasukBranch) {
      entries.push({ name: '과숙살', pillar: pos, basedOn: 'year' });
    }
  }

  // 상문살 (년지 기준, +2 위치)
  const sangmunBranch = (yearBranchIdx + 2) % 12;
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === sangmunBranch) {
      entries.push({ name: '상문살', pillar: pos, basedOn: 'year' });
    }
  }

  // 조객살 (년지 기준, +10 위치 = 상문살의 대칭)
  const jogaekBranch = (yearBranchIdx + 10) % 12;
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === jogaekBranch) {
      entries.push({ name: '조객살', pillar: pos, basedOn: 'year' });
    }
  }

  // 삼재는 원국 신살이 아닌 세운 신살 — fortune/yearly-fortune.ts에서 처리

  // 천형살 (天刑) — 년지 기준, +3 위치
  const cheonhyeongBranch = (yearBranchIdx + 3) % 12;
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === cheonhyeongBranch) {
      entries.push({ name: '천형살', pillar: pos, basedOn: 'year' });
    }
  }

  // 소모살 (小耗) — 년지 기준, +7 위치
  const somoBranch = (yearBranchIdx + 7) % 12;
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === somoBranch) {
      entries.push({ name: '소모살', pillar: pos, basedOn: 'year' });
    }
  }

  // 천곡살 (天哭, +6) / 천허살 (天虛, +5) — 년지 기준
  const cheongokBranch = (yearBranchIdx + 6) % 12;
  const cheonheoBranch = (yearBranchIdx + 5) % 12;
  for (const pos of positions) {
    if (pos === 'year') continue;
    const bIdx = branchIndices[pos];
    if (bIdx === cheongokBranch) {
      entries.push({ name: '천곡살', pillar: pos, basedOn: 'year' });
    }
    if (bIdx === cheonheoBranch) {
      entries.push({ name: '천허살', pillar: pos, basedOn: 'year' });
    }
  }

  // 홍란살 (년지 기준)
  const hongranBranch = HONGRAN_SAL[yearBranchIdx];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === hongranBranch) {
      entries.push({ name: '홍란살', pillar: pos, basedOn: 'year' });
    }
  }

  // 천복귀인 (년지 기준)
  const cheonbokBranch = CHEONBOK_GUIIN[yearBranchIdx];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === cheonbokBranch) {
      entries.push({ name: '천복귀인', pillar: pos, basedOn: 'year' });
    }
  }

  // 천구살 = 년지+10 (jogaekBranch와 동일 공식, 별개 신살)
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === jogaekBranch) {
      entries.push({ name: '천구관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 중혼살 = 년지+5
  const junghonBranch = (yearBranchIdx + 5) % 12;
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === junghonBranch) {
      entries.push({ name: '중혼관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 대패살
  const dapaeBranch = DAEPAE_SAL[yearBranchIdx];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === dapaeBranch) {
      entries.push({ name: '대패관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 팔패살
  const palpaeBranch = PALPAE_SAL[yearBranchIdx];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === palpaeBranch) {
      entries.push({ name: '팔패관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 낭적살
  const nangjeokBranch = NANGJEOK_SAL[yearBranchIdx];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === nangjeokBranch) {
      entries.push({ name: '낭적관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 절방살
  const jeolbangBranch = JEOLBANG_SAL[yearBranchIdx];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === jeolbangBranch) {
      entries.push({ name: '절방관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 천모살
  const cheonmoBranch = CHEONMO_SAL[yearBranchIdx];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === cheonmoBranch) {
      entries.push({ name: '천모관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 타모살
  const tamoBranch = TAMO_SAL[yearBranchIdx];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === tamoBranch) {
      entries.push({ name: '타모관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 농아살
  const nongaBranch = NONGA_SAL[yearBranchIdx];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === nongaBranch) {
      entries.push({ name: '농아관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 상충살 (sal13: 년지→지지 매핑, 표준 6충과 다름)
  const sangchungBranch = SANGCHUNG_SAL[yearBranchIdx];
  for (const pos of positions) {
    if (pos === 'year') continue;
    if (branchIndices[pos] === sangchungBranch) {
      entries.push({ name: '상충관살', pillar: pos, basedOn: 'year' });
    }
  }

  // ── sal09/sal10: 년지 기준 → 일지/시지 ──
  const sal09_10Targets: PillarPosition[] = noHour ? ['day'] : ['day', 'hour'];

  // 천의 (天宜: 년지 - 1 = 대상지지, 천의성 天醫와 별개)
  const cheonuiYearBranch = (yearBranchIdx - 1 + 12) % 12;
  for (const pos of sal09_10Targets) {
    if (branchIndices[pos] === cheonuiYearBranch) {
      entries.push({ name: '천의살', pillar: pos, basedOn: 'year' });
    }
  }

  // 안맹살 (계절 그룹 → 단일 지지)
  const anmaengBranch = ANMAENG_SAL[yearSeasonGroup];
  for (const pos of sal09_10Targets) {
    if (branchIndices[pos] === anmaengBranch) {
      entries.push({ name: '안맹관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 장명살 (년지 - 1 = 대상지지, cheonuiYearBranch와 동일 공식)
  for (const pos of sal09_10Targets) {
    if (branchIndices[pos] === cheonuiYearBranch) {
      entries.push({ name: '장명관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 단명살 (년지 → 지지, 巳寅辰未 3주기 반복)
  const danmyeongBranch = DANMYEONG_SAL[yearBranchIdx];
  for (const pos of sal09_10Targets) {
    if (branchIndices[pos] === danmyeongBranch) {
      entries.push({ name: '단명관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 직난살 (년지 → 1~2개 대상 지지)
  const jiknanBranches = JIKNAN_SAL[yearBranchIdx];
  for (const pos of sal09_10Targets) {
    const targetBranch = branchIndices[pos];
    if (jiknanBranches.includes(targetBranch)) {
      entries.push({ name: '직난관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 야제살 (계절 그룹 → 삼합 장성 위치)
  const yajeBranch = YAJE_SAL[yearSeasonGroup];
  for (const pos of sal09_10Targets) {
    if (branchIndices[pos] === yajeBranch) {
      entries.push({ name: '야제관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 무정살 (계절 그룹 → 복수 대상 지지)
  const mujeongBranches = MUJEONG_SAL[yearSeasonGroup];
  for (const pos of sal09_10Targets) {
    if (mujeongBranches.includes(branchIndices[pos])) {
      entries.push({ name: '무정관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 사계살 (계절 그룹 → 복수 대상 지지)
  const sagyeBranches = SAGYE_SAL[yearSeasonGroup];
  for (const pos of sal09_10Targets) {
    if (sagyeBranches.includes(branchIndices[pos])) {
      entries.push({ name: '사계관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 염왕살 (계절 그룹 → 복수 대상 지지)
  const yeomwangBranches = YEOMWANG_SAL[yearSeasonGroup];
  for (const pos of sal09_10Targets) {
    if (yeomwangBranches.includes(branchIndices[pos])) {
      entries.push({ name: '염왕관살', pillar: pos, basedOn: 'year' });
    }
  }

  // 건각살 (sal09 전용: 년지 → 일지만)
  if (GEONGAK_SAL[yearBranchIdx] === dayBranchIdx) {
    entries.push({ name: '건각관살', pillar: 'day', basedOn: 'year' });
  }

  // 결항살 (삼합 그룹 + 일주 sexagenary 매칭)
  const gyeolhangIndices = GYEOLHANG_INDICES[yearSamhapGroup];
  if (gyeolhangIndices.includes(fourPillars.day.sexagenaryIndex)) {
    entries.push({ name: '결항관살', pillar: 'day', basedOn: 'year' });
  }

  // 폭패살 (년지 → 대상 지지 O(1) 매핑)
  const pokpaeBranches = POKPAE_SAL[yearBranchIdx];
  if (pokpaeBranches) {
    for (const pos of positions) {
      if (pos === 'year') continue;
      if (pokpaeBranches.includes(branchIndices[pos])) {
        entries.push({ name: '폭패관살', pillar: pos, basedOn: 'year' });
      }
    }
  }

  // ── 관살류 (년지→일지) ──

  // 권설관살 (년지+9 = 일지)
  if ((yearBranchIdx + 9) % 12 === dayBranchIdx) {
    entries.push({ name: '권설관살', pillar: 'day', basedOn: 'year' });
  }

  // 수옥관살
  if (SUOK_SAL[yearBranchIdx] === dayBranchIdx) {
    entries.push({ name: '수옥관살', pillar: 'day', basedOn: 'year' });
  }

  // 탄함관살
  if (TANHAM_SAL[yearBranchIdx] === dayBranchIdx) {
    entries.push({ name: '탄함관살', pillar: 'day', basedOn: 'year' });
  }

  // 구문관살 (년지+3 = 일지)
  if ((yearBranchIdx + 3) % 12 === dayBranchIdx) {
    entries.push({ name: '구문관살', pillar: 'day', basedOn: 'year' });
  }

  // 단교관살
  if (DANGYO_SAL[yearBranchIdx] === dayBranchIdx) {
    entries.push({ name: '단교관살', pillar: 'day', basedOn: 'year' });
  }

  // 삼구관살 (계절그룹→일지)
  if (SAMGU_SAL[yearSeasonGroup] === dayBranchIdx) {
    entries.push({ name: '삼구관살', pillar: 'day', basedOn: 'year' });
  }

  // 천조관살 (삼합그룹→일지 배열)
  if (CHEONJO_SAL[yearSamhapGroup].includes(dayBranchIdx)) {
    entries.push({ name: '천조관살', pillar: 'day', basedOn: 'year' });
  }

  return entries;
}

/** 월지 기준 신살 */
function findMonthBranchSinsal(ctx: SinsalContext): SinsalEntry[] {
  const entries: SinsalEntry[] = [];
  const { fourPillars, positions, stemIndices, branchIndices, monthSeasonGroup, noHour } = ctx;
  const monthBranchIdx = branchIndices.month;

  // 월덕귀인 (월지의 삼합 그룹 기준)
  const monthGroup = getSamhapGroup(monthBranchIdx);
  const woldeokStemIdx = WOLDEOK_GUIIN[monthGroup];
  for (const pos of positions) {
    const sIdx = stemIndices[pos];
    if (sIdx === woldeokStemIdx) {
      entries.push({ name: '월덕귀인', pillar: pos, basedOn: 'month' });
    }
  }

  // 천덕귀인 (월지 기준, 천간 또는 지지 매칭)
  const cheondeok = CHEONDEOK_GUIIN[monthBranchIdx];
  if (cheondeok) {
    for (const pos of positions) {
      if (cheondeok.type === 'stem') {
        if (stemIndices[pos] === cheondeok.index) {
          entries.push({ name: '천덕귀인', pillar: pos, basedOn: 'month' });
        }
      } else {
        if (branchIndices[pos] === cheondeok.index) {
          entries.push({ name: '천덕귀인', pillar: pos, basedOn: 'month' });
        }
      }
    }
  }

  // 천의성 (월지 기준, 월지의 바로 앞 지지)
  const cheonuiBranch = (monthBranchIdx - 1 + 12) % 12;
  for (const pos of positions) {
    if (pos === 'month') continue;
    const bIdx = branchIndices[pos];
    if (bIdx === cheonuiBranch) {
      entries.push({ name: '천의성', pillar: pos, basedOn: 'month' });
    }
  }

  // 천희성 (월지 기준)
  const cheonhuiBranch = CHEONHUI_SEONG[monthBranchIdx];
  for (const pos of positions) {
    if (pos === 'month') continue;
    if (branchIndices[pos] === cheonhuiBranch) {
      entries.push({ name: '천희성', pillar: pos, basedOn: 'month' });
    }
  }

  // 황은대사 (월지 기준, 일지·시지만 비교)
  const hwangeunBranch = HWANGEUN_DAESA[monthBranchIdx];
  const hwangeunPositions: PillarPosition[] = noHour ? ['day'] : ['day', 'hour'];
  for (const pos of hwangeunPositions) {
    if (branchIndices[pos] === hwangeunBranch) {
      entries.push({ name: '황은대사', pillar: pos, basedOn: 'month' });
    }
  }

  // 급각살 (월지의 계절 그룹 기준)
  const geupgakBranches = GEUPGAK_SAL[monthSeasonGroup];
  for (const pos of positions) {
    if (pos === 'month') continue;
    const bIdx = branchIndices[pos];
    if (geupgakBranches.includes(bIdx)) {
      entries.push({ name: '급각살', pillar: pos, basedOn: 'month' });
    }
  }

  // 복음살 (월지와 동일한 지지가 다른 기둥에 있으면)
  for (const pos of positions) {
    if (pos === 'month') continue;
    if (branchIndices[pos] === monthBranchIdx) {
      entries.push({ name: '복음살', pillar: pos, basedOn: 'month' });
    }
  }

  // 월합 (월지→천간)
  const wolhapStem = WOLHAP[monthBranchIdx];
  for (const pos of positions) {
    if (stemIndices[pos] === wolhapStem) {
      entries.push({ name: '월합', pillar: pos, basedOn: 'month' });
    }
  }

  // 천옥살 (월지→천간)
  const cheonokStem = CHEONOK_SAL[monthBranchIdx];
  for (const pos of positions) {
    if (stemIndices[pos] === cheonokStem) {
      entries.push({ name: '천옥관살', pillar: pos, basedOn: 'month' });
    }
  }

  // 맹인살 (월지 계절 그룹 기반)
  const maenginBranch = MAENGIN_SAL[monthSeasonGroup];
  for (const pos of positions) {
    if (pos === 'month') continue;
    if (branchIndices[pos] === maenginBranch) {
      entries.push({ name: '맹인관살', pillar: pos, basedOn: 'month' });
    }
  }

  // 수화살
  const suhwaBranches = SUHWA_SAL[monthSeasonGroup];
  for (const pos of positions) {
    if (pos === 'month') continue;
    if (suhwaBranches.includes(branchIndices[pos])) {
      entries.push({ name: '수화관살', pillar: pos, basedOn: 'month' });
    }
  }

  // 심수살
  const simsuBranches = SIMSU_SAL[monthSeasonGroup];
  for (const pos of positions) {
    if (pos === 'month') continue;
    if (simsuBranches.includes(branchIndices[pos])) {
      entries.push({ name: '심수관살', pillar: pos, basedOn: 'month' });
    }
  }

  // 장군살
  const janggunBranches = JANGGUN_SAL[monthSeasonGroup];
  for (const pos of positions) {
    if (pos === 'month') continue;
    if (janggunBranches.includes(branchIndices[pos])) {
      entries.push({ name: '장군관살', pillar: pos, basedOn: 'month' });
    }
  }

  // 혈분살
  const hyeolbunBranches = HYEOLBUN_SAL[monthSeasonGroup];
  for (const pos of positions) {
    if (pos === 'month') continue;
    if (hyeolbunBranches.includes(branchIndices[pos])) {
      entries.push({ name: '혈분관살', pillar: pos, basedOn: 'month' });
    }
  }

  // 천혁살 (seasonal group → day pillar matching)
  const cheonhyeokIndices = CHEONHYEOK_INDICES[monthSeasonGroup];
  if (cheonhyeokIndices.includes(fourPillars.day.sexagenaryIndex)) {
    entries.push({ name: '천혁관살', pillar: 'day', basedOn: 'month' });
  }

  return entries;
}

/** 기둥쌍/복합 조건 신살 */
function findRelationSinsal(ctx: SinsalContext): SinsalEntry[] {
  const entries: SinsalEntry[] = [];
  const { positions, stemIndices, branchIndices, noHour, gender } = ctx;
  const dayBranchIdx = branchIndices.day;
  const yearBranchIdx = branchIndices.year;
  const monthBranchIdx = branchIndices.month;
  const yearStemIdx = stemIndices.year;
  const monthStemIdx = stemIndices.month;
  const GUIMUN_PAIRS = noHour ? ALL_GUIMUN_PAIRS_NO_HOUR : ALL_GUIMUN_PAIRS;

  // 현침살 (천간·지지에 해당 글자가 있으면 성립)
  for (const pos of positions) {
    const sIdx = stemIndices[pos];
    const bIdx = branchIndices[pos];
    if (HYEONCHIM_STEMS.includes(sIdx) || HYEONCHIM_BRANCHES.includes(bIdx)) {
      entries.push({ name: '현침살', pillar: pos, basedOn: pos });
    }
  }

  // 천라지망 (지지 쌍 검사)
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const b1 = branchIndices[positions[i]];
      const b2 = branchIndices[positions[j]];
      // 천라: 戌+亥
      if (CHEOLLA_BRANCHES.includes(b1) && CHEOLLA_BRANCHES.includes(b2) && b1 !== b2) {
        entries.push({ name: '천라지망', pillar: positions[i], basedOn: positions[j], description: '천라(戌亥)' });
      }
      // 지망: 辰+巳
      if (JIMANG_BRANCHES.includes(b1) && JIMANG_BRANCHES.includes(b2) && b1 !== b2) {
        entries.push({ name: '천라지망', pillar: positions[i], basedOn: positions[j], description: '지망(辰巳)' });
      }
    }
  }

  // 원진살 (쌍방향: 두 기둥 모두 원진살에 해당)
  const wonjinBranch = WONJIN_SAL[dayBranchIdx];
  for (const pos of positions) {
    if (pos === 'day') continue;
    if (branchIndices[pos] === wonjinBranch) {
      entries.push({ name: '원진살', pillar: 'day', basedOn: pos });
      entries.push({ name: '원진살', pillar: pos, basedOn: 'day' });
    }
  }

  // 귀문관살 (쌍방향: 모든 기둥쌍 체크, 양쪽 모두 등록)
  for (const [pos1, pos2] of GUIMUN_PAIRS) {
    const b1 = branchIndices[pos1];
    const b2 = branchIndices[pos2];
    if (GUIMUN_SAL[b1] === b2) {
      entries.push({ name: '귀문관살', pillar: pos1, basedOn: pos2 });
      entries.push({ name: '귀문관살', pillar: pos2, basedOn: pos1 });
    }
  }

  // 천소살 (년간의 보합 천간 = 월간 AND 해당 지지 = 월지)
  const cheoncho = CHEONSO_SAL[yearStemIdx];
  if (cheoncho && monthStemIdx === cheoncho.complementStemIdx && monthBranchIdx === cheoncho.branchIdx) {
    entries.push({ name: '천소관살', pillar: 'month', basedOn: 'year' });
  }

  // 격각살 (년지 + 2 = 월지)
  if ((yearBranchIdx + 2) % 12 === monthBranchIdx) {
    entries.push({ name: '격각살', pillar: 'month', basedOn: 'year' });
  }

  // 골파살 (년지→지지, 남녀 구분)
  if (gender) {
    const golpaTable = gender === 'male' ? GOLPA_SAL_MALE : GOLPA_SAL_FEMALE;
    const golpaBranch = golpaTable[yearBranchIdx];
    for (const pos of positions) {
      if (pos === 'year') continue;
      if (branchIndices[pos] === golpaBranch) {
        entries.push({ name: '골파관살', pillar: pos, basedOn: 'year' });
      }
    }

    // 철소살 (년지→지지, 남녀 구분)
    const cheolsoTable = gender === 'male' ? CHEOLSO_SAL_MALE : CHEOLSO_SAL_FEMALE;
    const cheolsoBranch = cheolsoTable[yearBranchIdx];
    for (const pos of positions) {
      if (pos === 'year') continue;
      if (branchIndices[pos] === cheolsoBranch) {
        entries.push({ name: '철소관살', pillar: pos, basedOn: 'year' });
      }
    }

    // 재가살 (여성 전용, 년지+6)
    if (gender === 'female') {
      const jaegaBranch = JAEGA_SAL[yearBranchIdx];
      for (const pos of positions) {
        if (pos === 'year') continue;
        if (branchIndices[pos] === jaegaBranch) {
          entries.push({ name: '재가관살', pillar: pos, basedOn: 'year' });
        }
      }
    }
  }

  // 자결살 (子↔午, 卯↔酉 2충만 검사, 모든 기둥쌍)
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const b1 = branchIndices[positions[i]];
      const b2 = branchIndices[positions[j]];
      for (const [a, b] of JAGYEOL_PAIRS) {
        if ((b1 === a && b2 === b) || (b1 === b && b2 === a)) {
          entries.push({ name: '자결관살', pillar: positions[i], basedOn: positions[j] });
          entries.push({ name: '자결관살', pillar: positions[j], basedOn: positions[i] });
        }
      }
    }
  }

  // 곡배살 (6충 전체 검사: 子↔午, 丑↔未, 寅↔申, 卯↔酉, 辰↔戌, 巳↔亥)
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const b1 = branchIndices[positions[i]];
      const b2 = branchIndices[positions[j]];
      if (Math.abs(b1 - b2) === 6) {
        entries.push({ name: '곡배관살', pillar: positions[i], basedOn: positions[j] });
        entries.push({ name: '곡배관살', pillar: positions[j], basedOn: positions[i] });
      }
    }
  }

  // 야체관살 (년지 계절그룹→일지 배열)
  const yacheBranches = YACHE_SAL[ctx.yearSeasonGroup];
  if (yacheBranches.includes(dayBranchIdx)) {
    entries.push({ name: '야체관살', pillar: 'day', basedOn: 'year' });
  }

  // 백일관살 (삼위 교차: 四驛↔四庫, 四正↔四驛, 四庫↔四正)
  if (
    (SAMWI_YEOK.includes(yearBranchIdx) && SAMWI_GO.includes(dayBranchIdx)) ||
    (SAMWI_JEONG.includes(yearBranchIdx) && SAMWI_YEOK.includes(dayBranchIdx)) ||
    (SAMWI_GO.includes(yearBranchIdx) && SAMWI_JEONG.includes(dayBranchIdx))
  ) {
    entries.push({ name: '백일관살', pillar: 'day', basedOn: 'year' });
  }

  // 화상관살 (4개 지지 모두 같은 삼위 그룹)
  if (!noHour) {
    const allBranches = [branchIndices.year, branchIndices.month, branchIndices.day, branchIndices.hour];
    if (
      allBranches.every(b => SAMWI_YEOK.includes(b)) ||
      allBranches.every(b => SAMWI_JEONG.includes(b)) ||
      allBranches.every(b => SAMWI_GO.includes(b))
    ) {
      entries.push({ name: '화상관살', pillar: 'day', basedOn: 'year' });
    }
  }

  return entries;
}

/**
 * 일반 신살을 검출한다.
 *
 * 사주 원국의 네 기둥(년·월·일·시)을 분석하여 해당하는 일반 신살 목록을 반환한다.
 *
 * @param fourPillars - 사주 네 기둥
 * @param noHour - 시주 미포함 여부 (true이면 년·월·일만 분석)
 * @returns 검출된 일반 신살 배열
 */
export function findGeneralSinsal(fourPillars: FourPillars, noHour?: boolean, gender?: 'male' | 'female'): SinsalEntry[] {
  const positions = noHour ? ALL_PILLAR_POSITIONS.slice(0, 3) : ALL_PILLAR_POSITIONS;

  const stemIndices: Record<PillarPosition, number> = {
    year: stemIndex(fourPillars.year.stem),
    month: stemIndex(fourPillars.month.stem),
    day: stemIndex(fourPillars.day.stem),
    hour: stemIndex(fourPillars.hour.stem),
  };
  const branchIndices: Record<PillarPosition, number> = {
    year: branchIndex(fourPillars.year.branch),
    month: branchIndex(fourPillars.month.branch),
    day: branchIndex(fourPillars.day.branch),
    hour: branchIndex(fourPillars.hour.branch),
  };

  const ctx: SinsalContext = {
    fourPillars,
    positions,
    stemIndices,
    branchIndices,
    yearSeasonGroup: getSeasonGroup(branchIndices.year),
    monthSeasonGroup: getSeasonGroup(branchIndices.month),
    yearSamhapGroup: getSamhapGroup(branchIndices.year),
    noHour,
    gender,
  };

  return [
    ...findDayStemSinsal(ctx),
    ...findDayPillarSinsal(ctx),
    ...findYearBranchSinsal(ctx),
    ...findMonthBranchSinsal(ctx),
    ...findRelationSinsal(ctx),
  ];
}
